import React, { useCallback } from 'react';
import { Divider, Stack, Box } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { Paper, Typography, SizingMultiplier } from '@ocx/ui';
import { getRewardExpiresIn, getRewardExpiresInMessage, getVouchersExpirationData, Offer } from '@ocx/data-offers';
import { rewardExpirationMessages } from '@ocx/feat-offers';
import { getOfferCardMarketingContent, OfferCard, OfferCardLabel } from '@ocx/ui-offers';

import { messages } from './messages';
import { HEADER_CONTENT_COLOR } from './constants';
import { impressionTrackingCtxFromOffer } from '../../modules/offers/tracking.types';

export type ReadyToUseProps = {
  offers: Offer[];
  getOfferOnClick: (offer: Offer) => (() => unknown) | undefined;
  loading: boolean;
};

export const ReadyToUse = ({ offers, getOfferOnClick, loading }: ReadyToUseProps) => {
  const intl = useIntl();

  const renderOffer = useCallback(
    (offer: Offer) => {
      const onClick = getOfferOnClick(offer);
      const label: OfferCardLabel = {
        type: 'Reward',
        voucherCount: offer.voucherCount,
      };
      const marketingContent = getOfferCardMarketingContent(offer);
      const { unfulfilledVouchersCount, voucherExpirationDate } = getVouchersExpirationData(offer.vouchers);

      const rewardExpiresIn = getRewardExpiresInMessage({
        expiresIn: getRewardExpiresIn({ rewardExpiresIn: voucherExpirationDate || offer.expireAt }),
        isMultipleVouchers: !!unfulfilledVouchersCount,
        formatMessage: (key, values) => intl.formatMessage(rewardExpirationMessages[key], values),
      });

      return (
        <OfferCard
          key={offer.id}
          impressionTrackingCtx={impressionTrackingCtxFromOffer(offer)}
          marketingContent={marketingContent}
          onClick={onClick}
          label={label}
          accessoryType="none"
          // Ready to use is not technically designed to show progress or diff
          pointsDiff={null}
          progress={null}
          rewardExpiresIn={rewardExpiresIn}
        />
      );
    },
    [getOfferOnClick, intl],
  );

  return (
    <Stack minHeight="100%" sx={{ background: ({ palette }) => palette.readyToUse.background }}>
      <Stack
        gap={SizingMultiplier.sm}
        px={SizingMultiplier.md}
        py={({ mixins }) => `${mixins.toolbar.minHeight}px`}
        textAlign="center"
        color={HEADER_CONTENT_COLOR}>
        <Typography variant="h3" component="h2" textTransform="none">
          <FormattedMessage {...messages.headerTitle} />
        </Typography>
        <Typography variant="body2" maxWidth={296} alignSelf="center">
          <FormattedMessage {...messages.headerSubtitle} />
        </Typography>
      </Stack>
      <Paper
        sx={{
          borderBottomRightRadius: 0,
          borderBottomLeftRadius: 0,
          flex: 1,
          overflowY: 'auto',
        }}>
        {!offers.length && !loading ? (
          <Box textAlign="center" p={SizingMultiplier.md}>
            <Typography variant="subtitle1" gutterBottom>
              <FormattedMessage {...messages.noOffersTitle} />
            </Typography>
            <Typography color="textSecondary">
              <FormattedMessage {...messages.noOffersDescription} />
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Divider sx={{ mx: SizingMultiplier.md }} />}>{offers.map(renderOffer)}</Stack>
        )}
      </Paper>
    </Stack>
  );
};
